import { computed, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import { sizeService, type Size } from "../services/size";

type SizeSaveError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
};

export function useSize() {
  const { t } = useI18n();
  const sizes = ref<Size[]>([]);
  const searchQuery = ref("");
  const showModal = ref(false);
  const selectedSize = ref<Size | null>(null);
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});

  const form = ref<Size>({
    code: "",
    name: "",
    description: "",
    status: 1,
  });

  const fetchSizes = async () => {
    loading.value = true;
    try {
      sizes.value = await sizeService.getSizes();
    } catch (error) {
      console.error("Failed to fetch sizes:", error);
      Swal.fire({
        icon: "error",
        title: t("size_page.load_failed"),
        text: t("size_page.load_failed_text"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredSizes = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return sizes.value;

    return sizes.value.filter((size) =>
      [size.code, size.name, size.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const stats = computed(() => {
    const total = sizes.value.length;
    const active = sizes.value.filter(
      (size) => String(size.status ?? 1) !== "0" && String(size.status ?? 1) !== "false",
    ).length;

    return {
      total,
      active,
      inactive: Math.max(total - active, 0),
    };
  });

  const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);

  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("size_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("size_page.items_per_page") },
  }));

  watch(
    filteredSizes,
    (newValue) => {
      const totalCount = newValue.length > 0 ? newValue.length : 1000;
      pageSizeOptions.value = ["10", "25", "50", "100", String(totalCount)];
    },
    { immediate: true },
  );

  const resetForm = () => {
    form.value = {
      code: "",
      name: "",
      description: "",
      status: 1,
    };
    errors.value = {};
  };

  const openCreateModal = () => {
    selectedSize.value = null;
    resetForm();
    showModal.value = true;
  };

  const openEditModal = (size: Size) => {
    selectedSize.value = size;
    errors.value = {};
    form.value = {
      id: size.id,
      code: size.code || "",
      name: size.name || "",
      description: size.description || "",
      status: String(size.status) === "0" || String(size.status) === "false" ? 0 : 1,
    };
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    errors.value = {};
  };

  const updateField = (field: keyof Size, value: string | number | undefined) => {
    form.value = {
      ...form.value,
      [field]: value,
    };
  };

  const saveSize = async () => {
    loading.value = true;
    errors.value = {};
    try {
      const payload = {
        code: form.value.code || undefined,
        name: form.value.name || "",
        description: form.value.description || undefined,
        status: Number(form.value.status ?? 1),
      };

      if (selectedSize.value?.id) {
        await sizeService.updateSize(selectedSize.value.id, payload);
      } else {
        await sizeService.createSize(payload);
      }

      await fetchSizes();
      await Swal.fire({
        icon: "success",
        title: selectedSize.value ? t("size_page.updated_title") : t("size_page.created_title"),
        timer: 1400,
        showConfirmButton: false,
      });
      closeModal();
    } catch (error: unknown) {
      const requestError = error as SizeSaveError;
      console.error(error);
      if (requestError.response?.status === 422) {
        errors.value = requestError.response.data?.errors || {};
      } else {
        const message =
          requestError.response?.data?.message ||
          Object.values(requestError.response?.data?.errors || {}).flat()[0] ||
          t("size_page.save_failed_text");
        Swal.fire({
          icon: "error",
          title: t("size_page.save_failed"),
          text: message as string,
        });
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteSize = async (size: Size) => {
    if (!size?.id) return;

    const result = await Swal.fire({
      title: t("size_page.delete_title"),
      text: t("size_page.delete_text", { name: size.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("size_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("size_page.deleting_title"),
        text: t("size_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await sizeService.deleteSize(size.id);
      await fetchSizes();

      Swal.fire({
        icon: "success",
        title: t("size_page.deleted_title"),
        text: t("size_page.deleted_text", { name: size.name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("size_page.delete_failed"),
        text: t("size_page.delete_failed_text"),
      });
    }
  };

  return {
    searchQuery,
    filteredSizes,
    stats,
    showModal,
    selectedSize,
    form,
    errors,
    loading,
    paginationConfig,
    fetchSizes,
    openCreateModal,
    openEditModal,
    closeModal,
    updateField,
    saveSize,
    deleteSize,
  };
}
